<template>
  <footer>
    <div class="w-full viewport-gutter py-4">
      <div class="container flex justify-between">
        <div>
          <a
            v-if="!loading"
            href="#"
            class="text-sm xl:text-lg cursor-pointer"
            @click.prevent="showExitPopup = true"
          >
            {{ $t('components.footer.returnToLH') }}
          </a>
        </div>

        <CancelRedemptionPopup v-model="showExitPopup" />

        <div>
          <div
            v-on-clickaway="closeLanguageSwitcher"
            class="lang-switch relative"
            :class="{ open: showLanguageSwitcher }"
          >
            <div
              class="trigger inline-flex items-center justify-center leading-none text-primary cursor-pointer text-sm xl:text-lg"
              @click="toggleLanguageSwitcher"
            >
              <div class="mr-3">
                <img src="~images/psp/globe.svg" :alt="$t('components.footer.switchLanguage')">
              </div>
              <span class="inline">{{ $t(`locales.${currentLocale}`) }}</span>
            </div>
            <div
              class="dropdown hidden absolute right-0 bottom-0 mb-11 px-4 py-1 bg-white shadow-2 text-right text-sm xl:text-lg"
            >
              <ul>
                <li
                  v-for="(locale, i) in availableLocales"
                  :key="`Lang${i}`"
                  class="leading-none cursor-pointer py-1 xl:py-2"
                  @click.prevent="changeLocale(locale)"
                >
                  {{ $t(`locales.${locale}`) }}
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="bg-primary text-white px-4 py-8 xl:py-6">
      <div class="container">
        <nav class="w-full -mb-4">
          <ul class="flex text-sm justify-center xl:justify-start xl:text-lg">
            <li class="leading-tight truncate pb-4 mr-8">
              <a :href="imprintLink" target="_blank">{{ $t('components.footer.links.imprint') }}</a>
            </li>
            <li class="leading-tight truncate pb-4 mr-8">
              <a :href="privacyLink" target="_blank">{{ $t('components.footer.links.privacy') }}</a>
            </li>
            <li class="leading-tight truncate pb-4">
              <a
                :href="`https://www.lufthansa.com/xx/${termsOfUseLocale}/book-and-manage/travel-information/flight-voucher/terms-of-use`"
                target="_blank"
              >
                {{ $t("components.footer.links.termsOfUse") }}
              </a>
            </li>
          </ul>
        </nav>
      </div>
    </div>

    <div class="px-4 py-6">
      <div class="container">
        <div class="w-full flex flex-col items-center justify-between xl:flex-row">
          <div class="pb-4 xl:pb-0">
            <img src="~images/psp/logo-blue.svg" class="w-33 h-6" alt="Lufthansa">
          </div>
          <div>
            <img src="~images/psp/star-alliance.png" class="star-alliance__logo" alt="Star Alliance Logo">
          </div>
        </div>
      </div>
    </div>
  </footer>
</template>

<script lang="ts">
import { mixin as clickaway } from 'vue-clickaway'
import CancelRedemptionPopup from '~app/psp/lufthansa/cancel-redemption-popup.vue'
import Footer from '~app/psp/base/footer'

export default Footer.extend({
  components: {
    CancelRedemptionPopup,
  },
  mixins: [clickaway],

  data(): {
    showLanguageSwitcher: boolean;
    showExitPopup: boolean;
  } {
    return {
      showLanguageSwitcher: false,
      showExitPopup: false,
    }
  },
  /*
  mounted() {
    window.onbeforeunload = (event: Event) => {
      // TODO: every window.location or a href with outside link needs
      // to skip the code below in order to prevent the popup.
      // (since this is a wanted redirection compared to a page refresh or page close)
      // This can be done, so that for every link we dispatch an action,
      // that sets a state before actually doing the redirect.
      // Then in this function, if that variable is set to true
      // don't show a popup warning on leave.
      // event.preventDefault() // per the standard
      // event.returnValue = '' // required for Chrome
    }
  },
  */
  computed: {
    imprintLink(): string {
      switch (this.$i18n.locale) {
        case 'de':
          return 'https://www.lufthansa.com/xx/de/impressum'
        case 'fr':
          return 'https://www.lufthansa.com/xx/fr/mentions-legales'
        case 'it':
          return 'https://www.lufthansa.com/it/it/dati-societari'
        default:
          return 'https://www.lufthansa.com/xx/en/imprint'
      }
    },
    privacyLink(): string {
      switch (this.$i18n.locale) {
        case 'de':
          return 'https://www.lufthansa.com/xx/de/informationen-zum-datenschutz'
        case 'fr':
          return 'https://www.lufthansa.com/xx/fr/informations-concernant-la-protection-des-donnees'
        case 'it':
          return 'https://www.lufthansa.com/xx/it/informazioni-sulla-protezione-dei-dati'
        default:
          return 'https://www.lufthansa.com/xx/en/information-on-data-protection'
      }
    },
  },
  methods: {
    toggleLanguageSwitcher() {
      this.showLanguageSwitcher = !this.showLanguageSwitcher
    },
    closeLanguageSwitcher() {
      this.showLanguageSwitcher = false
    },
    changeLocale(locale: string) {
      this.$store.dispatch('locale/set', { locale })
    },
  },
})
</script>

<style lang="scss" scoped>
.star-alliance__logo {
  width: 205px;
}

.lang-switch {
  .dropdown {
    &::before {
      position: absolute;
      right: 1rem;
      bottom: -10px;
      width: 0;
      height: 0;
      content: '';
      -webkit-filter: drop-shadow(0 10px 0 rgba(0, 0, 0, 0.15));
      filter: drop-shadow(0 2px 3px rgba(0, 0, 0, 0.15));
      border-color: #fff transparent transparent transparent;
      border-style: solid;
      border-width: 10px 15px 0 15px;
    }
    &::after {
      // triangel (before) shadow removal inside dropdown
      position: absolute;
      right: 1rem;
      bottom: 0;
      width: 30px;
      height: 5px;
      content: '';
      background: white;
    }
  }
  &.open {
    .dropdown {
      @apply block;

      display: block;
    }
  }
}
</style>
